import React, { useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SendOutlinedIcon from "@mui/icons-material/Send";

import palette from "../../styles/palette";

/**
 * Bolhas + Enter-para-enviar + auto-scroll. Extraído do painel de autoria
 * revertido, que era o único lugar do app onde isso estava bem feito.
 *
 * O componente não conhece "professor", "assistente" nem "ia": recebe `mine`
 * já resolvido. Vocabulário de domínio é do chamador — é o que permite que a
 * Entrevista e o playground de análise de vídeo usem o mesmo quadro sem que um
 * empurre o léxico do outro.
 */
export interface ChatMessage {
  key: React.Key;
  /** true = falou o usuário logado; alinha à direita. */
  mine: boolean;
  text: string;
  /** Conteúdo visual opcional, mantendo o texto original no histórico. */
  content?: React.ReactNode;
  /** Linha de rodapé da bolha: origem, aviso, carimbo. */
  caption?: React.ReactNode;
}

interface Props {
  messages: ChatMessage[];
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  busy?: boolean;
  busyLabel?: React.ReactNode;
  placeholder?: string;
  /** Presente = botão com rótulo; ausente = ícone de enviar. */
  sendLabel?: string;
  multiline?: boolean;
  /** Altura fixa da área de mensagens; sem isso ela ocupa o espaço do flex. */
  height?: number | string;
  /** Placeholder do quadro quando não há mensagem nenhuma. */
  empty?: React.ReactNode;
  footer?: React.ReactNode;
  /** Entre as mensagens e o campo: resposta guiada, sugestões, atalhos. */
  beforeInput?: React.ReactNode;
  /** Ação opcional ao lado esquerdo do campo de mensagem. */
  inputStart?: React.ReactNode;
  /** Permite ao consumidor enviar anexos mesmo com o texto vazio. */
  hasAttachments?: boolean;
}

export default function ConversationPane({
  messages,
  value,
  onChange,
  onSend,
  busy = false,
  busyLabel = "pensando…",
  placeholder,
  sendLabel,
  multiline = false,
  height,
  empty,
  footer,
  beforeInput,
  inputStart,
  hasAttachments = false,
}: Props) {
  const scroller = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages, busy]);

  const podeEnviar = !busy && (value.trim().length > 0 || hasAttachments);
  // Sem nada para mostrar, o quadro não ocupa altura: o playground de análise
  // começa como um campo solto e só vira conversa depois da primeira pergunta.
  const mostrarMensagens = messages.length > 0 || busy || Boolean(empty);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      {mostrarMensagens && (
        <Box
          ref={scroller}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 2,
            py: 1.5,
            ...(height ? { height, flex: "none" } : {}),
          }}
        >
        {messages.length === 0 && !busy && empty}
        {messages.map((m) => (
          <Box
            key={m.key}
            sx={{
              mb: 1,
              p: 1.25,
              borderRadius: 1.5,
              whiteSpace: "pre-wrap",
              background: m.mine ? palette.brandSoft : palette.muted,
              ml: m.mine ? 6 : 0,
              mr: m.mine ? 0 : 6,
            }}
          >
            {m.content ?? <Typography variant="body2">{m.text}</Typography>}
            {m.caption && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: palette.textMuted }}
              >
                {m.caption}
              </Typography>
            )}
          </Box>
        ))}
        {busy && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" sx={{ color: palette.textMuted }}>
              {busyLabel}
            </Typography>
          </Box>
        )}
        </Box>
      )}

      {beforeInput}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1.5 }}>
        {inputStart}
        <TextField
          size="small"
          fullWidth
          multiline={multiline}
          maxRows={multiline ? 4 : undefined}
          placeholder={placeholder}
          value={value}
          disabled={busy}
          inputProps={{ "aria-label": placeholder }}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (podeEnviar) onSend();
            }
          }}
        />
        {sendLabel ? (
          <Button variant="outlined" size="small" disabled={!podeEnviar} onClick={onSend}>
            {sendLabel}
          </Button>
        ) : (
          <IconButton aria-label="Enviar" disabled={!podeEnviar} onClick={onSend}>
            <SendOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {footer}
    </Box>
  );
}
